import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { usePullToRefresh } from '../../../hooks/usePullToRefresh'
import { Icon } from '../../atoms/Icon'
import {
  PullToRefreshWrapper,
  PullIndicator,
  PullIndicatorContent,
  PullIndicatorText,
  PullableContent,
} from './DataTable.styles'

interface PullToRefreshContainerProps {
  children: ReactNode
  onRefresh?: () => Promise<void>
  refreshing?: boolean
  enabled?: boolean
}

export function PullToRefreshContainer({
  children,
  onRefresh,
  refreshing,
  enabled = true,
}: PullToRefreshContainerProps) {
  const { t } = useTranslation('ui')

  const {
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
    progress,
    canRelease,
  } = usePullToRefresh({
    onRefresh: onRefresh ?? (async () => {}),
    enabled: enabled && !!onRefresh,
    refreshing,
    threshold: 80,
    maxPull: 150,
  })

  // If pull-to-refresh is not enabled, just render children
  if (!onRefresh) {
    return <>{children}</>
  }

  return (
    <PullToRefreshWrapper ref={containerRef as React.RefObject<HTMLDivElement>}>
      {/* Pull indicator at top */}
      {(isPulling || isRefreshing || pullDistance > 0) && (
        <PullIndicator
          $pullDistance={isRefreshing ? 60 : pullDistance}
          $canRelease={canRelease}
          $isRefreshing={isRefreshing}
        >
          <PullIndicatorContent
            $progress={progress}
            $isRefreshing={isRefreshing}
          >
            <Icon name={isRefreshing ? 'loader' : 'arrow-down'} size="medium" />
            <PullIndicatorText>
              {isRefreshing
                ? t('dataTable.mobile.refreshing')
                : canRelease
                  ? t('dataTable.mobile.releaseToRefresh')
                  : t('dataTable.mobile.pullToRefresh')}
            </PullIndicatorText>
          </PullIndicatorContent>
        </PullIndicator>
      )}

      {/* Content that moves down when pulling */}
      <PullableContent $translateY={isRefreshing ? 60 : pullDistance}>
        {children}
      </PullableContent>
    </PullToRefreshWrapper>
  )
}

export default PullToRefreshContainer
